import styles from './Tabs.module.css';

import type { FC, KeyboardEvent } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { tabStop, tabAfterKey } from 'src/utils/tabKeys';

import { Typography } from '../themed/Typography';

type TabsProps = {
  activeTab: string;
  onChangeTab: (tab: string) => void;
  tabs: string[];
  /**
   * Prefix for the DOM ids that tie each tab to the panel it controls. The screen renders the
   * panel as `<div role="tabpanel" id={tabPanelId(id)} aria-labelledby={tabId(id, activeTab)}>`.
   */
  id: string;
  /** Names the tab list for assistive tech: the id of the screen's visible heading, when it has one… */
  'aria-labelledby'?: string;
  /** …or a phrase, when it has none. */
  'aria-label'?: string;
};

/** The DOM id of one tab — what the panel's `aria-labelledby` points at. */
export const tabId = (prefix: string, tab: string): string =>
  `${prefix}-tab-${tab.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;

/** The DOM id of the one panel a tab list controls (only the selected tab's content is rendered). */
export const tabPanelId = (prefix: string): string => `${prefix}-panel`;

/**
 * A row of tabs (WAI-ARIA tabs pattern, app#216).
 *
 * The tabs used to be `<div onClick>`: nothing for a keyboard to land on, nothing for a screen
 * reader to announce. Now each is a button with `role="tab"` inside a `tablist`; the selected one
 * carries the Tab stop and the arrows move the selection (roving focus — one stop for the whole
 * row, so a keyboard user does not have to walk through every tab to get past them). The key
 * decisions live in `src/utils/tabKeys.ts`, where they have unit tests.
 */
export const Tabs: FC<TabsProps> = (props) => {
  const { activeTab, onChangeTab, tabs, id, 'aria-labelledby': labelledBy, 'aria-label': label } = props;
  const { t } = useTranslation();
  const stop = tabStop(tabs, activeTab);
  const panel = tabPanelId(id);

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    const next = tabAfterKey(event.key, tabs, activeTab);

    if (next === null) {
      return;
    }

    // The browser would otherwise scroll the page on an arrow key.
    event.preventDefault();
    onChangeTab(next);
    // The focus follows the selection so the next arrow press continues from where the user is.
    // The tablist's children are the tabs in `tabs` order, by construction.
    const button = event.currentTarget.parentElement?.children[tabs.indexOf(next)];
    if (button instanceof HTMLElement) button.focus();
  };

  return (
    <div className={styles.container} role="tablist" aria-labelledby={labelledBy} aria-label={label}>
      {tabs.map((tab) => {
        const active = tab === activeTab;
        return (
          <button
            key={tab}
            id={tabId(id, tab)}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={active ? panel : undefined}
            tabIndex={tab === stop ? 0 : -1}
            className={classNames(styles.item, active && styles.active)}
            onClick={() => onChangeTab(tab)}
            onKeyDown={handleKeyDown}
          >
            <Typography as="span" fontWeight="500" color={active ? 'grey' : 'extraLightWhite'}>
              {t(tab)}
            </Typography>
          </button>
        );
      })}
    </div>
  );
};
